/**
 * DateTime utilities for Europe/Sarajevo timezone.
 */

const TIMEZONE = 'Europe/Sarajevo';

/**
 * Format date to DD.MM.YYYY
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('hr-HR', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format time to HH:MM (24-hour)
 */
export const formatTime = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleTimeString('hr-HR', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format datetime to DD.MM.YYYY HH:MM
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleString('hr-HR', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format datetime to DD.MM.YYYY HH:MM with timezone label
 */
export const formatDateTimeWithTZ = (dateInput) => {
  if (!dateInput) return '';
  const formatted = formatDateTime(dateInput);
  if (!formatted) return '';
  
  const date = new Date(dateInput);
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  const isDST = date.getTimezoneOffset() < stdOffset;
  
  return `${formatted} ${isDST ? 'CEST' : 'CET'}`;
};

/**
 * Format short date to DD.MM.
 */
export const formatDateShort = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('hr-HR', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit'
  });
};

/**
 * Get relative date label (Danas, Sutra, Jučer, or formatted date)
 */
export const getRelativeDateLabel = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.toLocaleString('en-US', { timeZone: TIMEZONE }));
  today.setHours(0, 0, 0, 0);
  
  const inputDate = new Date(date.toLocaleString('en-US', { timeZone: TIMEZONE }));
  inputDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.round((inputDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Danas';
  if (diffDays === 1) return 'Sutra';
  if (diffDays === -1) return 'Jučer';
  if (diffDays > 1 && diffDays <= 7) return `Za ${diffDays} dana`;
  if (diffDays < -1 && diffDays >= -7) return `Prije ${Math.abs(diffDays)} dana`;
  
  return formatDate(date);
};

/**
 * Get today's date string for input[type="date"] (YYYY-MM-DD)
 */
export const getTodayInputValue = () => {
  const now = new Date();
  const sarajevoDate = new Date(now.toLocaleString('en-US', { timeZone: TIMEZONE }));
  return sarajevoDate.toISOString().split('T')[0];
};

/**
 * Time ago helper
 */
export const getTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "upravo sada";
  if (diffMins < 60) return `prije ${diffMins} min`;
  if (diffHours < 24) return `prije ${diffHours}h`;
  if (diffDays < 7) return `prije ${diffDays}d`;
  return formatDate(date);
};

export { TIMEZONE };
